import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { ADDRESS_FIELDS, IAddressData } from '@df/session/api/address-book/address';
import { AddressFieldsetComponentDef } from '@df/ui/form/address-fieldset.component.def';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { BehaviorSubject } from 'rxjs';
import { NCmsDelivery } from '../delivery/modal/delivery-modal.component';

export const NAME_PREFIXES = ['Mr', 'Mrs', 'Miss'];

@Component({
    selector: 'address-fieldset',
    templateUrl: 'address-fieldset.html',
    styleUrls: ['address-fieldset.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class AddressFieldsetComponent extends AddressFieldsetComponentDef {
    @Input()
    showOnlyAddress = false;

    @Input()
    showCountry?: 1 | 2;

    @Input()
    twoColumn = false;

    @Input()
    lockedPostcode?: string;

    @Input()
    lockedAddress?: Partial<IAddressData>;

    @Input()
    loadCountriesOnInit = false;

    protected query?: string;

    // public prefixes = NAME_PREFIXES;

    protected searchVisible$ = new BehaviorSubject(false);
    private router = inject(Router);

    /**
     * @depreciated
     * @see https://tomandco.atlassian.net/browse/TI-257
     */
    @Output()
    saveAddress = new EventEmitter();

    /**
     * @see https://tomandco.atlassian.net/browse/TI-257
     */
    onInputBlur(): void {
        this.saveAddress.emit();
    }

    get doubleColumnSize() {
        return this.twoColumn ? 2 : 1;
    }

    @ViewChild('searchInput', { read: ElementRef })
    protected searchInput?: ElementRef;

    override form = inject(ControlContainer) as NgForm;

    private dfModalService = inject(DfModalService);

    override onSearcherSelectionApplied(): void {
        if (this.data.street_2) {
            this.data.street_1 = this.data.street_1 + ' ' + this.data.street_2;
            this.data.street_2 = undefined;
        }
        this.onDataChange();
        this.clearSearch();
    }

    override ngOnInit() {
        super.ngOnInit();
        if (this.loadCountriesOnInit) {
            this.loadCountries();
        }
        if (this.searcher) {
            this.subscriptions.add(this.searcher.status.subscribe(() => this.onSearcherStatusChange()));
        }
    }

    override ngOnChanges(changes) {
        super.ngOnChanges(changes);
        this.onDataChange();
    }

    ngAfterViewInit() {
        if (this.form) {
            this.subscriptions.add(this.form.valueChanges!.subscribe(() => this.onDataChange()));
        }
    }

    protected triggerSearcher() {
        if (this.searcher?.query && this.searcher.query.length > 2) {
            this.searcher.search();
        }
    }

    protected clearSearch() {
        if (this.searcher) {
            this.searcher.query = undefined;
            if (this.searchVisible$.value) {
                this.searchVisible$.next(false);
            }
        }
    }

    private onSearcherStatusChange() {
        if (this.searcher) {
            if (this.searcher.query && this.searcher.hints?.length) {
                if (!this.searchVisible$.value) {
                    this.searchVisible$.next(true);
                }
            }
            this.detectChanges();
        }
    }

    protected get postcode() {
        if (this.lockedPostcode) {
            return this.lockedPostcode;
        }
        return this.data.postcode;
    }

    protected set postcode(code: string | undefined) {
        if (!this.lockedPostcode) {
            this.data.postcode = code;
        }
    }

    protected changePostcode() {
        this.router.navigateByUrl('/basket');
        this.timeoutService.setTimeout(() => {
            this.dfModalService.open(undefined, NCmsDelivery.MODAL_ID);
        }, 1000);
    }

    private onDataChange() {
        if (this.lockedPostcode) {
            this.data.postcode = this.lockedPostcode;
            this.data.customer_address_id = undefined;
        }
        if (this.lockedAddress) {
            ADDRESS_FIELDS.forEach(field => {
                if (!['firstname', 'lastname', 'telephone', 'company'].includes(field)) {
                    this.data[field] = this.lockedAddress![field];
                }
            });
            this.data.customer_address_id = undefined;
        }
    }

    private async loadCountries() {
        await this.dfApiConfigService.loadCountries();
        this.syncCountries();
        this.detectChanges();
    }
}
